import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import { ActionButton } from "@/components/shared/action-buttons"
import { useIndexProps } from "./page-props"
import { useTranslation } from "react-i18next"
import { Inbox, Pencil, PlusCircle, Trash } from "lucide-react"
import { buttonVariants } from "@/components/ui/button-variants"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { SheetPage, SheetPageTrigger } from "@/components/ui/sheet-page"
import { Link, usePathParam } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation(["preference_templates", "common"])
  const { templates } = useIndexProps()

  return (
    <>
      <LearnerPageChrome action={<CreateTemplateSheet />} />
      <PageBody role="region">
        {templates.length === 0 ? (
          <Card>
            <CardContent>
              <Empty data-testid="preference-templates-empty">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("index.empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("index.empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <CreateTemplateSheet />
                </EmptyContent>
              </Empty>
            </CardContent>
          </Card>
        ) : (
          <Card className="p-6">
            <ul className="grid w-full grid-cols-2 gap-4">
              {templates.map(({ label, value }) => (
                <li key={value}>
                  <Card data-testid="preference-template-card">
                    <CardHeader>
                      <CardTitle>{label}</CardTitle>
                    </CardHeader>
                    <CardFooter className="flex flex-row flex-wrap gap-4">
                      <Button asChild>
                        <Link href={`./preference_templates/${value}/edit`}>
                          <Pencil />
                          {t("index.edit")}
                        </Link>
                      </Button>
                      <ConfirmationDialog
                        action={
                          <Link
                            className={buttonVariants({
                              variant: "destructive",
                            })}
                            href={`./preference_templates/${value}`}
                            method="delete"
                            as={Button}
                            role="link"
                          >
                            {t("common:actions.confirm")}
                          </Link>
                        }
                      >
                        <ConfirmationDialogTrigger asChild>
                          <Button variant={"destructive-outline"}>
                            <Trash />
                            {t("common:actions.delete")}
                          </Button>
                        </ConfirmationDialogTrigger>
                      </ConfirmationDialog>
                    </CardFooter>
                  </Card>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </PageBody>
    </>
  )
}

function CreateTemplateSheet() {
  const schoolId = usePathParam("schoolId")
  const { t } = useTranslation("preference_templates")
  return (
    <SheetPage href={`/learners/schools/${schoolId}/preference_templates/new`}>
      <SheetPageTrigger asChild>
        <ActionButton variant="solid" Icon={PlusCircle}>
          {t("index.create")}
        </ActionButton>
      </SheetPageTrigger>
    </SheetPage>
  )
}
